/* ============================================================
   Sweets by Ree — Main JavaScript
   Premium Motion System: Lenis + GSAP + IntersectionObserver
   ============================================================ */

import Lenis from 'lenis';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register GSAP plugin
gsap.registerPlugin(ScrollTrigger);

// -----------------------------------------------------------
// 1. Reduced Motion Detection
// -----------------------------------------------------------
const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// -----------------------------------------------------------
// 2. Lenis Smooth Scroll (disabled if reduced motion)
// -----------------------------------------------------------
let lenis: Lenis | null = null;

if (!prefersReducedMotion) {
  lenis = new Lenis({
    duration: 1.2,
    easing: (t: number) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
    wheelMultiplier: 1,
    touchMultiplier: 1.5,
  });

  lenis.on('scroll', ScrollTrigger.update);

  gsap.ticker.add((time) => {
    if (lenis) lenis.raf(time * 1000);
  });
  gsap.ticker.lagSmoothing(0);
}

// -----------------------------------------------------------
// 3. Reveal System — IntersectionObserver
// -----------------------------------------------------------
function initRevealSystem() {
  const observerOptions = {
    root: null,
    rootMargin: '0px 0px -60px 0px',
    threshold: 0.1,
  };

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      }
    });
  }, observerOptions);

  const revealSelector = '.motion-element, .motion-fade, .feature-card, .pricing-card, .h2-fade, .h3-fade, .eyebrow-expand, .eyebrow-line, .eyebrow-text, .text-mask-inner, .pull-quote-text, .caption-text, .process-connector, .star-icon, .back-to-top, .lead-form-success';

  document.querySelectorAll(revealSelector).forEach((el) => {
    revealObserver.observe(el);
  });

  // Immediate reveal for elements already in the viewport on load
  // (ensures content is visible in headless environments, no-JS fallbacks, and above-fold content)
  const runImmediateReveal = () => {
    const vh = window.innerHeight;
    document.querySelectorAll(revealSelector).forEach((el) => {
      if (el.classList.contains('is-visible')) return;
      const rect = el.getBoundingClientRect();
      if (rect.top < vh && rect.bottom > 0) {
        el.classList.add('is-visible');
        revealObserver.unobserve(el);
      }
    });
  };

  requestAnimationFrame(runImmediateReveal);
  setTimeout(runImmediateReveal, 50); // fallback for masked/overflow-hidden elements
}

// -----------------------------------------------------------
// 4. Navbar Scroll Behavior
// -----------------------------------------------------------
function initNavbar() {
  const navbar = document.querySelector('[data-navbar]');
  if (!navbar) return;

  let lastScroll = 0;

  const updateNavbar = () => {
    const currentScroll = window.scrollY || window.pageYOffset;

    if (currentScroll > 80) {
      navbar.classList.add('is-scrolled');
    } else {
      navbar.classList.remove('is-scrolled');
    }

    lastScroll = currentScroll;
  };

  window.addEventListener('scroll', updateNavbar, { passive: true });
  updateNavbar();
}

// -----------------------------------------------------------
// 5. Back-to-Top Button
// -----------------------------------------------------------
function initBackToTop() {
  const btn = document.querySelector('[data-back-to-top]');
  if (!btn) return;

  const toggle = () => {
    const scrollY = window.scrollY || window.pageYOffset;
    if (scrollY > 600) {
      btn.classList.add('is-visible');
    } else {
      btn.classList.remove('is-visible');
    }
  };

  window.addEventListener('scroll', toggle, { passive: true });
  toggle();

  btn.addEventListener('click', (e) => {
    e.preventDefault();
    if (lenis) {
      lenis.scrollTo(0, { duration: 1.2 });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
}

// -----------------------------------------------------------
// 6. Order Form Handling
// -----------------------------------------------------------
function initOrderForm() {
  const form = document.querySelector('[data-order-form]') as HTMLFormElement | null;
  const successPanel = document.querySelector('[data-form-success]');

  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const honeypot = form.querySelector('[name="bot-field"]') as HTMLInputElement | null;
    if (honeypot && honeypot.value) {
      console.warn('Bot submission detected.');
      return;
    }

    const submitBtn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Sending...';
    }

    // Collect data
    const formData = new FormData(form);
    const payload: Record<string, string> = {};
    formData.forEach((value, key) => {
      if (key !== 'bot-field') payload[key] = String(value);
    });

    // Simulate submission (replace with real endpoint)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));

      // Hide form, show success
      form.style.opacity = '0';
      form.style.transition = 'opacity 400ms cubic-bezier(0.22, 1, 0.36, 1)';

      setTimeout(() => {
        form.classList.add('hidden');
        if (successPanel) {
          successPanel.classList.remove('hidden');
          requestAnimationFrame(() => {
            successPanel.classList.add('is-visible');
          });
        }
      }, 400);
    } catch (err) {
      console.error('Form submission error:', err);
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Place My Order';
      }
      alert('Something went wrong. Please text Ree at (256) 299-3656 instead.');
    }
  });
}

// -----------------------------------------------------------
// 7. Smooth Anchor Links
// -----------------------------------------------------------
function initAnchorLinks() {
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      const href = anchor.getAttribute('href');
      if (!href || href === '#') return;

      const target = document.querySelector(href);
      if (!target) return;

      e.preventDefault();
      if (lenis) {
        lenis.scrollTo(target as HTMLElement, { offset: -80, duration: 1.2 });
      } else {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });
}

// -----------------------------------------------------------
// 8. GSAP Scroll-Driven Light Effects (desktop only)
// -----------------------------------------------------------
function initGSAPExtras() {
  if (prefersReducedMotion) return;

  const mm = gsap.matchMedia();

  mm.add('(min-width: 768px)', () => {
    // Parallax on hero layers if present
    const heroLayers = document.querySelectorAll('[data-parallax]');
    heroLayers.forEach((layer) => {
      const speed = parseFloat(layer.getAttribute('data-parallax') || '1');
      gsap.to(layer, {
        y: () => -(window.innerHeight * (speed - 1) * 0.4),
        ease: 'none',
        scrollTrigger: {
          trigger: '[data-hero-scene]',
          start: 'top top',
          end: 'bottom top',
          scrub: 0.8,
        },
      });
    });

    // Horizontal parallax for gallery if present
    const hParallaxLeft = document.querySelectorAll('[data-h-parallax="left"]');
    const hParallaxRight = document.querySelectorAll('[data-h-parallax="right"]');
    hParallaxLeft.forEach((el) => {
      gsap.to(el, { x: -40, ease: 'none', scrollTrigger: { trigger: el.closest('section'), start: 'top bottom', end: 'bottom top', scrub: 1 } });
    });
    hParallaxRight.forEach((el) => {
      gsap.to(el, { x: 40, ease: 'none', scrollTrigger: { trigger: el.closest('section'), start: 'top bottom', end: 'bottom top', scrub: 1 } });
    });
  });
}

// -----------------------------------------------------------
// 9. Mobile Menu Toggle
// -----------------------------------------------------------
function initMobileMenu() {
  const toggle = document.querySelector('[data-mobile-toggle]');
  const menu = document.querySelector('[data-mobile-menu]');
  if (!toggle || !menu) return;

  toggle.addEventListener('click', () => {
    const isOpen = menu.classList.toggle('hidden');
    toggle.setAttribute('aria-expanded', String(!isOpen));
    document.body.classList.toggle('overflow-hidden', !isOpen);
  });

  menu.querySelectorAll('a').forEach((link) => {
    link.addEventListener('click', () => {
      menu.classList.add('hidden');
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
    });
  });
}

// -----------------------------------------------------------
// Initialize Everything
// -----------------------------------------------------------
document.addEventListener('DOMContentLoaded', () => {
  initRevealSystem();
  initNavbar();
  initBackToTop();
  initOrderForm();
  initAnchorLinks();
  initGSAPExtras();
  initMobileMenu();
});
